/*
 * The wishing well — static base + animated water + optional fishing rod
 * when the player is fishing.
 */

import { Container, Graphics, Text } from 'pixi.js';
import type { FishState } from '@/state/types';
import { WORLD } from '@/render/palette';

export interface WellView {
  container: Container;
  update(t: number, fish: FishState): void;
}

export function createWell(): WellView {
  const root = new Container();
  root.position.set(WORLD.wellX, WORLD.wellY - 20);

  // Ring of stones around the well.
  const ringStones = new Graphics();
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    const rx = Math.cos(a) * 55;
    const ry = 30 + Math.sin(a) * 38; // offset 30 from well center to land below it
    ringStones.ellipse(rx, ry, 9, 6).fill(0x7a6f63);
  }
  root.addChild(ringStones);

  // Soft ground glow around the well.
  const glow = new Graphics();
  glow.circle(0, 20, 180).fill({ color: 0xffc56b, alpha: 0.05 });
  glow.circle(0, 20, 110).fill({ color: 0xffc56b, alpha: 0.08 });
  root.addChild(glow);

  // Stone base of well.
  const stoneBase = new Graphics();
  stoneBase.roundRect(-28, -10, 56, 26, 4).fill(0x8a8074);
  // Stone lines.
  stoneBase.rect(-26, -4, 14, 1.5).fill(0x5e554b);
  stoneBase.rect(-10, -4, 14, 1.5).fill(0x5e554b);
  stoneBase.rect(6, -4, 18, 1.5).fill(0x5e554b);
  stoneBase.rect(-22, 5, 12, 1.5).fill(0x5e554b);
  stoneBase.rect(-6, 5, 16, 1.5).fill(0x5e554b);
  stoneBase.rect(12, 5, 12, 1.5).fill(0x5e554b);
  root.addChild(stoneBase);

  // Water surface (static dark layer + lighter ellipse on top).
  const water = new Graphics();
  water.ellipse(0, -8, 24, 7).fill(0x1c3a5a);
  water.ellipse(0, -9, 19, 4).fill(0x3a6a8a);
  // Idle highlight (visible when not fishing).
  water.ellipse(-8, -10, 5, 1).fill({ color: 0xffffff, alpha: 0.4 });
  root.addChild(water);

  // Animated fishing layer — ripples, line, bobber.
  const fishingLayer = new Graphics();
  root.addChild(fishingLayer);

  // Roof posts.
  const roof = new Graphics();
  roof.rect(-22, -42, 3, 32).fill(0x5c3d28);
  roof.rect(19, -42, 3, 32).fill(0x5c3d28);
  // Triangular roof.
  roof
    .moveTo(-32, -40)
    .lineTo(0, -60)
    .lineTo(32, -40)
    .closePath()
    .fill(0x6b4423);
  // Roof seam lines.
  roof
    .moveTo(-24, -45)
    .lineTo(0, -55)
    .moveTo(0, -55)
    .lineTo(24, -45)
    .stroke({ color: 0x000000, alpha: 0.2, width: 0.8 });
  root.addChild(roof);

  // Nibble exclamation mark — drawn over everything.
  const nibbleMark = new Text({
    text: '!',
    style: {
      fontFamily: 'Fraunces, serif',
      fontSize: 16,
      fontWeight: '800',
      fill: 0xffc56b,
    },
  });
  nibbleMark.anchor.set(0.5);
  nibbleMark.visible = false;
  root.addChild(nibbleMark);

  function update(t: number, fish: FishState): void {
    fishingLayer.clear();

    const fishing = fish.active && (fish.state === 'waiting' || fish.state === 'nibble');
    if (!fishing) {
      nibbleMark.visible = false;
      return;
    }

    const intensity = fish.state === 'nibble' ? 1 : 0.3;

    // Ripples — three concentric rings phased out over time.
    for (let i = 0; i < 3; i++) {
      const phase = (t * 0.002 + i * 0.7) % 1;
      const r = phase * 20;
      const alpha = (1 - phase) * 0.5 * intensity;
      fishingLayer
        .ellipse(0, -9, r, r * 0.3)
        .stroke({ color: 0xffffff, alpha, width: 1 });
    }

    // Bobber position.
    const bobX = Math.sin(t * 0.003) * 2;
    let bobY = -9 + Math.sin(t * 0.005) * 1;
    if (fish.state === 'nibble') bobY += Math.sin(t * 0.04) * 2;

    // Fishing line from rod tip down to bobber.
    fishingLayer
      .moveTo(20, -35)
      .lineTo(bobX, bobY)
      .stroke({ color: 0xffffff, alpha: 0.4, width: 0.6 });

    // Bobber.
    fishingLayer.circle(bobX, bobY, 2.5).fill(0xc9514c);
    fishingLayer.circle(bobX, bobY, 1.2).fill(0xfff4dc);

    // Rod.
    fishingLayer
      .moveTo(5, -30)
      .lineTo(20, -35)
      .stroke({ color: 0x3a2410, width: 1.2 });

    if (fish.state === 'nibble') {
      const pulse = 1 + Math.sin(t * 0.02) * 0.15;
      nibbleMark.scale.set(pulse);
      nibbleMark.position.set(0, -38);
      nibbleMark.visible = true;
    } else {
      nibbleMark.visible = false;
    }
  }

  return { container: root, update };
}
